import React, { useState, useCallback, useRef, useEffect } from "react";
import { UserData } from "@/entities/users/types";
import { useReport } from "@/entities/reporting/hooks";
import {
  LeadAnalysisData,
  LeadAnalysisDirectReport,
  ReportType,
  SettingsPeriod
} from "@/entities/reporting/types";
import LeadAnalysis from "@/components/layouts/LeadAnalysis/LeadAnalysis";
import { useDirectReportsCheck } from "@/components/shared/useDirectReportsCheck";
import { debounce } from "lodash";

interface Props {
  userData?: UserData;
  isAE: boolean;
}

const LeadAnalysisWrapper: React.FC<Props> = ({ userData, isAE }) => {
  const [reportPeriod, setReportPeriod] = useState(
    SettingsPeriod.LAST_12_MONTHS
  );

  // Keep track of the last successful data using ref to avoid re-render loops
  const lastValidReportRef = useRef<LeadAnalysisData | null>(null);

  const { report, isLoading } = useReport(
    ReportType.LEAD_ANALYSIS,
    reportPeriod
  );

  const { hasDirectReports } = useDirectReportsCheck<LeadAnalysisDirectReport>(
    userData?.id,
    reportPeriod,
    !isAE,
    {
      reportType: ReportType.LEAD_ANALYSIS,
      responseKey: "leadsAnalysis",
      checkEmptyFn: (data) => (data?.overall?.length ?? 0) > 0
    }
  );

  const debouncedPeriodSelect = useCallback(
    debounce((period: SettingsPeriod) => {
      setReportPeriod(period);
    }, 300),
    []
  );

  const currentReport = report?.leadsAnalysis ? report : undefined;

  useEffect(() => {
    if (currentReport) {
      lastValidReportRef.current = currentReport;
    }
  }, [currentReport]);

  // Use current data if available, otherwise fall back to last valid data
  const leadAnalysisReport: LeadAnalysisData =
    currentReport ?? lastValidReportRef.current ?? undefined;

  return (
    <>
      <LeadAnalysis
        data={leadAnalysisReport}
        userData={userData}
        period={reportPeriod}
        periodSelect={debouncedPeriodSelect}
        isLoading={isLoading}
        isAE={isAE}
        drillDownHidden={!hasDirectReports}
      />
    </>
  );
};

export default LeadAnalysisWrapper;
